import { createEntityAdapter, createSelector } from "@reduxjs/toolkit";
import { apiSlice } from "../../apiSlice";
import {
  selectInventorySpareLotFilterState,
} from "../../../store/inventory/inventorySpareLotListSlice";

/* =====================
   CONSTANTS
===================== */
const DEFAULT_PAGE_SIZE = 1;

/* =====================
   ENTITY ADAPTER
===================== */
const inventorySpareLotAdapter = createEntityAdapter({
  selectId: (lot) => lot.id,
});

const initialState = inventorySpareLotAdapter.getInitialState({
  nextCursor: null,
  hasMore: false,
  totalCount: 0,
});

/* =====================
   HELPERS
===================== */
const buildQueryString = (base, params) => {
  const queryParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      queryParams.append(key, value);
    }
  });

  return `${base}?${queryParams.toString()}`;
};

/** Extract only API-relevant filters (status, search, etc.) */
const extractApiFilters = (filterState) =>
  Object.fromEntries(
    Object.entries(filterState).filter(
      ([key, value]) =>
        !["cursor", "pagination"].includes(key) &&
        value !== null &&
        value !== ""
    )
  );

/* =====================
   API SLICE
===================== */
export const inventorySpareLotSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    /* ---------- LIST ---------- */
    getInventorySpareLotList: builder.query({
      query: ({ inventory_id, cursor = 0, filters = {} }) =>
        buildQueryString(`/inventory/spares/${inventory_id}`, {
          cursor,
          pagination: DEFAULT_PAGE_SIZE,
          ...filters,
        }),

      transformResponse: (response) => {
        const items = Array.isArray(response?.data) ? response.data : [];

        const normalized = inventorySpareLotAdapter.setAll(
          { ...initialState },
          items
        );

        return {
          ...normalized,
          nextCursor: response?.nextCursor ?? null,
          hasMore: Boolean(response?.hasMore),
          totalCount: response?.totalCount ?? items.length,
        };
      },

      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const { inventory_id, filters = {} } = queryArgs;
        return `${endpointName}(${inventory_id}):${JSON.stringify(filters)}`;
      },

      merge: (currentCache, incoming) => {
        if (!incoming?.ids?.length) {
          currentCache.nextCursor = incoming?.nextCursor ?? null;
          currentCache.hasMore = Boolean(incoming?.hasMore);
          return;
        }

        const incomingItems = incoming.ids
          .map((id) => incoming.entities[id])
          .filter(Boolean);

        inventorySpareLotAdapter.upsertMany(currentCache, incomingItems);

        currentCache.nextCursor = incoming.nextCursor;
        currentCache.hasMore = Boolean(incoming.hasMore);
        currentCache.totalCount = currentCache.ids.length;
      },

      forceRefetch({ currentArg, previousArg }) {
        return previousArg?.inventory_id !== currentArg?.inventory_id;
      },

      providesTags: (result, error, arg) =>
        result
          ? [
              { type: "InventorySpareLotList", id: arg.inventory_id },
              ...result.ids.map((id) => ({
                type: "InventorySpareLot",
                id,
              })),
            ]
          : [{ type: "InventorySpareLotList", id: arg.inventory_id }],
    }),

    /* ---------- DETAILS ---------- */
    getInventorySpareLotById: builder.query({
      query: ({ inventory_id, request_id }) =>
        `/inventory/spares/${inventory_id}/${request_id}`,

      transformResponse: (response) => {
        const data = response?.data;
        if (!data) return null;

        const highlights = Object.keys(data)
          .filter((key) => key.startsWith("description_") && data[key])
          .sort((a, b) => {
            const aNum = Number(a.split("_")[1]);
            const bNum = Number(b.split("_")[1]);
            return aNum - bNum;
          })
          .map((key) => data[key]);

        return {
          ...data,
          highlights,
        };
      },

      providesTags: (result) =>
        result ? [{ type: "InventorySpareLot", id: result.id }] : [],
    }),

    /* ---------- UPDATE ---------- */
    updateInventorySpareLot: builder.mutation({
      query: ({ data }) => ({
        url: `/inventory/spares/hot_selling`,
        method: "PATCH",
        body: data,
      }),

      async onQueryStarted(
        { id, inventory_id, data },
        { dispatch, getState, queryFulfilled }
      ) {
        /** 1️⃣ Get active filters (IMPORTANT FIX) */
        const filterState =
          selectInventorySpareLotFilterState(getState());

        const activeFilters = extractApiFilters(filterState);

        /* ---------- optimistic detail ---------- */
        const detailPatch = dispatch(
          inventorySpareLotSlice.util.updateQueryData(
            "getInventorySpareLotById",
            { inventory_id, request_id: id },
            (draft) => {
              if (draft) Object.assign(draft, data);
            }
          )
        );

        /* ---------- optimistic list (FILTER-AWARE) ---------- */
        const listPatch = dispatch(
          inventorySpareLotSlice.util.updateQueryData(
            "getInventorySpareLotList",
            {
              inventory_id,
              cursor: 0,
              filters: activeFilters,
            },
            (draft) => {
              inventorySpareLotAdapter.upsertOne(draft, {
                id,
                ...data,
              });
            }
          )
        );

        try {
          const { data: serverResult } = await queryFulfilled;

          if (serverResult?.id) {
            dispatch(
              inventorySpareLotSlice.util.updateQueryData(
                "getInventorySpareLotById",
                {
                  inventory_id,
                  request_id: serverResult.id,
                },
                (draft) => Object.assign(draft, serverResult)
              )
            );

            dispatch(
              inventorySpareLotSlice.util.updateQueryData(
                "getInventorySpareLotList",
                {
                  inventory_id,
                  cursor: 0,
                  filters: activeFilters,
                },
                (draft) => {
                  inventorySpareLotAdapter.upsertOne(
                    draft,
                    serverResult
                  );
                }
              )
            );
          }
        } catch {
          detailPatch.undo();
          listPatch.undo();
        }
      },

      invalidatesTags: (result) =>
        result
          ? [
              { type: "InventorySpareLot", id: result.id },
              {
                type: "InventorySpareLotList",
                id: result.inventory_id,
              },
            ]
          : [],
    }),
  }),
  overrideExisting: false,
});

/* =====================
   HOOKS
===================== */
export const {
  useGetInventorySpareLotListQuery,
  useGetInventorySpareLotByIdQuery,
  useUpdateInventorySpareLotMutation,
} = inventorySpareLotSlice;

/* =====================
   SELECTORS (FACTORIES)
===================== */

/* ---- query result ---- */
export const makeSelectInventorySpareLotQueryResult = (inventory_id) =>
  createSelector(
    [selectInventorySpareLotFilterState, (state) => state],
    (filter, state) => {
      const queryArgs = {
        inventory_id,
        cursor: filter.cursor ?? 0,
        filters: extractApiFilters(filter),
      };

      return inventorySpareLotSlice.endpoints.getInventorySpareLotList.select(
        queryArgs
      )(state);
    }
  );

/* ---- normalized data ---- */
export const makeSelectInventorySpareLotData = (inventory_id) =>
  createSelector(
    [makeSelectInventorySpareLotQueryResult(inventory_id)],
    (result) => result?.data ?? initialState
  );

/* ---- adapter selectors ---- */
export const makeInventorySpareLotSelectors = (inventory_id) =>
  inventorySpareLotAdapter.getSelectors((state) =>
    makeSelectInventorySpareLotData(inventory_id)(state)
  );

/* ---- pagination ---- */
export const makeSelectInventorySpareLotPagination = (inventory_id) =>
  createSelector([makeSelectInventorySpareLotData(inventory_id)], (data) => ({
    nextCursor: data.nextCursor,
    hasMore: data.hasMore,
    totalCount: data.ids.length,
  }));

/* ---- status ---- */
export const makeSelectInventorySpareLotStatus = (inventory_id) =>
  createSelector(
    [makeSelectInventorySpareLotQueryResult(inventory_id)],
    (result) => ({
      isLoading: result?.isLoading ?? false,
      isFetching: result?.isFetching ?? false,
      isSuccess: result?.isSuccess ?? false,
      isError: result?.isError ?? false,
      error: result?.error ?? null,
    })
  );
